import { FooterSection, Header } from './sections';
import './sections/style.css';
import ReactGA from 'react-ga4';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SignInButton } from '@clerk/clerk-react';
import SEO from '../../components/common/SEO';
import {breadcrumbSchema} from '../../utils/structuredData';

const SlackIntegration = () => {
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'production') {
      ReactGA.initialize('G-ZB9N7T58NE');
      ReactGA.send({ hitType: 'pageview', page: '/slack', title: 'Slack integration landing' });
    }
  }, []);

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Slack Integration', url: '/slack' }
  ]);

  return (
    <div className="min-w-full homePageContainer relative !bg-black">
      <SEO
        title="Slack Integration - CadencePro.ai | Real-Time Project Notifications"
        description="Stay aligned with automated Slack notifications from CadencePro. Get real-time updates for strategy maps, work units, sprints, and tasks. Customizable triggers and smart frequencies."
        canonical="/slack"
        keywords="slack integration, project notifications, cadencepro slack, team collaboration, automated project updates, slack notifications"
        ogImage="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/slack-seeklogo-2.png"
        structuredData={breadcrumbs}
      />
      <Header />

      <main className="w-full flex justify-center px-6 py-12 md:px-12 md:py-16 bg-gradient-to-b from-black via-gray-900/30 to-black">
        {/* Background accents (match SignUpForm) */}
        <div className="pointer-events-none absolute -top-6 left-2 w-72 h-48 opacity-40 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute bottom-[1050px] right-0 w-72 h-48 opacity-30 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-16 right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-[780px] right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-6 w-64 h-44 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute bottom-44 left-1/4 w-72 h-52 opacity-20 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />

        <article className="relative z-10 max-w-5xl w-full leading-7 text-gray-300 space-y-12">
            {/* Hero */}
            <header className="space-y-6 text-center">
              <div className="inline-flex items-center rounded-full border border-gray-700 bg-gray-900/60 px-3 py-1 text-xs text-gray-300">
                Slack integration
              </div>
            <div className="flex items-center justify-center gap-3">
              <img
                alt="Slack"
                className="w-10 h-10"
                src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/slack-seeklogo-2.png"
              />
              <span className="text-gray-500">×</span>
              <img
                alt="Cadence Pro"
                className="w-10 h-10 rounded"
                src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
              />
            </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Stay aligned with Slack notifications from Cadence Pro
              </h1>
              <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
                Automated, configurable updates for strategy maps, work units, sprints and tasks. Send
                them to the channels and people who need to know—without adding more meetings.
              </p>
              <div className="flex items-center justify-center gap-3 pt-2">
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 text-sm font-medium transition-colors"
                >
                  Book a demo
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center rounded-md border border-gray-700 hover:border-gray-600 text-gray-200 px-5 py-2.5 text-sm font-medium"
                >
                  Learn how it works
                </a>
              </div>
              <div className="pt-2 text-sm text-gray-400">
                <span className="mr-1">Already using Cadence?</span>
                <SignInButton mode="modal" afterSignInUrl="/strategymaps" afterSignUpUrl="/signup">
                  <button className="text-blue-400 hover:text-blue-300 underline">Log in</button>
                </SignInButton>
              </div>
            </header>

            {/* Value bullets */}
            <section aria-labelledby="benefits" className="space-y-4">
              <h2 id="benefits" className="text-xl md:text-2xl font-bold text-white">
                What you’ll get
              </h2>
              <ul className="grid md:grid-cols-2 gap-4">
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Real‑time notifications</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Instant alerts when work changes—across strategy maps, work units, sprints, and tasks.
                  </p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Target channels and users</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Post to specific channels or DM selected teammates. Keep updates relevant by audience.
                  </p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Customizable triggers</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Choose events and scope them to selected entities—or apply to all active items.
                  </p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Smart frequencies</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Send immediately for urgent items, or batch into daily/weekly digests to reduce noise.
                  </p>
                </li>
              </ul>
            </section>

            {/* What we send */}
            <section aria-labelledby="examples" className="space-y-4">
              <h2 id="examples" className="text-xl md:text-2xl font-bold text-white">
                Example notifications
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-sm text-gray-400 mb-2">Strategy maps & work units</p>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>New strategy map or work unit created</li>
                    <li>Status or owner changes</li>
                    <li>Progress summaries and milestone completions</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-sm text-gray-400 mb-2">Sprints</p>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Current sprint kickoff reminders</li>
                    <li>Mid‑sprint health summaries</li>
                    <li>End‑of‑sprint wrap‑ups</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-sm text-gray-400 mb-2">Tasks</p>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Task assigned or reassigned</li>
                    <li>Status changed, blocked, or completed</li>
                    <li>Due‑soon and overdue reminders</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-sm text-gray-400 mb-2">Digest messages</p>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Daily, weekly, biweekly, or monthly summaries</li>
                    <li>Top changes across projects and teams</li>
                    <li>Mention‑ready highlights to share in stand‑ups</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How it works */}
            <section id="how-it-works" aria-labelledby="how" className="space-y-4">
              <h2 id="how" className="text-xl md:text-2xl font-bold text-white">
                How it works
              </h2>
              <ol className="grid md:grid-cols-3 gap-4">
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">1) Connect your workspace</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Authorize Cadence Pro in Slack. You decide which channels or people can receive messages.
                  </p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">2) Pick channels and people</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Choose public channels, private channels you’re in, or individual teammates.
                  </p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">3) Add triggers</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Select the entity type, scope to specific items or all active ones, set frequency, and save.
                  </p>
                </li>
              </ol>
              <p className="text-sm text-gray-400">
                You can enable/disable Slack notifications globally, toggle individual triggers, reconnect, or disconnect anytime.
              </p>
            </section>

            {/* Why it helps */}
            <section aria-labelledby="benefit" className="space-y-4">
              <h2 id="benefit" className="text-xl md:text-2xl font-bold text-white">
                Why teams love this
              </h2>
              <ul className="grid md:grid-cols-3 gap-4">
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Fewer status meetings</p>
                  <p className="text-sm text-gray-400 mt-1">Crisp updates in Slack keep momentum high and calendars clear.</p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Faster decisions</p>
                  <p className="text-sm text-gray-400 mt-1">Bring project context to the conversation where work already happens.</p>
                </li>
                <li className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-base font-semibold text-gray-200">Less noise</p>
                  <p className="text-sm text-gray-400 mt-1">Choose only the events and cadence that matter to each channel.</p>
                </li>
              </ul>
            </section>

            {/* Permissions & privacy */}
            <section aria-labelledby="permissions" className="space-y-4">
              <h2 id="permissions" className="text-xl md:text-2xl font-bold text-white">
                Permissions, privacy, and control
              </h2>
              <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4 space-y-3 text-sm text-gray-300">
                <p>
                  We request only the minimum permissions needed to deliver notifications and to power channel/user selectors.
                  We do not read message content. You can remove the integration or any trigger at any time.
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li>Post messages to channels or DMs you explicitly select</li>
                  <li>Read channel and user names to power selectors</li>
                  <li>Store minimal Slack identifiers to route notifications</li>
                </ul>
                <p className="text-gray-400">
                  See our <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link> for details.
                </p>
              </div>
            </section>

            {/* Support & pricing */}
            <section aria-labelledby="support" className="space-y-4">
              <h2 id="support" className="text-xl md:text-2xl font-bold text-white">
                Support and availability
              </h2>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-gray-400">Pricing</p>
                  <p className="text-gray-200 font-medium mt-1">Free</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-gray-400">Support</p>
                  <p className="text-gray-200 font-medium mt-1">
                    <Link to="/help-center" className="text-blue-400 hover:text-blue-300 underline">Help Center</Link>
                    <span className="text-gray-500 mx-2">•</span>
                    <a href="mailto:karan@cadencepro.ai" className="text-blue-400 hover:text-blue-300 underline">karan@cadencepro.ai</a>
                  </p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                  <p className="text-gray-400">Languages</p>
                  <p className="text-gray-200 font-medium mt-1">English</p>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="pt-2">
              <div className="rounded-xl border border-gray-800 bg-gradient-to-r from-blue-600/10 to-purple-600/10 p-6 text-center">
                <p className="text-lg font-semibold text-white">Ready to see Cadence Pro with Slack?</p>
                <p className="text-sm text-gray-300 mt-1">
                  Book a demo and we’ll walk you through connecting Slack in Settings → Integrations.
                </p>
                <div className="pt-4">
                  <Link
                    to="/book-a-demo"
                    className="inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 text-sm font-medium transition-colors"
                  >
                    Book a demo
                  </Link>
                </div>
                <div className="pt-3 text-sm text-gray-400">
                  <span className="mr-1">Already have an account?</span>
                  <SignInButton mode="modal" afterSignInUrl="/strategymaps" afterSignUpUrl="/signup">
                    <button className="text-blue-400 hover:text-blue-300 underline">Log in</button>
                  </SignInButton>
                </div>
              </div>
            </section>
        </article>
      </main>

      <FooterSection />
    </div>
  );
};

export default SlackIntegration;